import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { TagConfig } from '@blaze-runtime/engine-domain';
import { PropertiesConfigInputContainerItemActionModel } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { FormatValueModule } from '@blaze-shared/shared-feature';
import { ScrollerComponent } from '@blaze-shared/shared-ui';
import { StyleSheetTarget } from '@blaze-shared/theming-domain';

import { TagsColumnValueComponent } from '../../../components/tags-column-value/tags-column-value.component';
import { MapCard, MapCardStyles } from '../map.models';

@Component({
  selector: 'be-map-card',
  imports: [
    AsyncPipe,
    NgClass,
    NgStyle,
    MatIconModule,
    FormatValueModule,
    ScrollerComponent,
    TagsColumnValueComponent,
  ],
  templateUrl: './map-card.component.html',
  styleUrls: ['./map-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapCardComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly schemaService = inject(SchemaService);

  datasetRow = input.required<Record<string, unknown>>();
  tagConfig = input<TagConfig>();
  card = input.required<MapCard>();
  cssStyle = input<Partial<MapCardStyles>>();
  getActionClasses =
    input<(StyleSheetTargetOrId: StyleSheetTarget | string | undefined) => string[]>();

  action = output<PropertiesConfigInputContainerItemActionModel>();

  image = linkedSignal({
    source: () => ({
      card: this.card(),
      datasetRow: this.datasetRow(),
    }),
    computation: ({ card, datasetRow }) => {
      return card.image && datasetRow
        ? (datasetRow[card.image.name] as string | undefined)
        : undefined;
    },
  });

  onImageLoadingError(): void {
    this.image.set(undefined);
    this.cdr.markForCheck();
  }

  onAction(action: PropertiesConfigInputContainerItemActionModel): void {
    this.action.emit(action);
  }

  getActionElementStyles(
    action: PropertiesConfigInputContainerItemActionModel,
  ): Record<string, string | undefined> {
    if (action.styleSheetTarget || action.styleSheetId) {
      return {};
    }

    const cssStyle = this.cssStyle();
    return {
      'background-color': cssStyle?.buttonFill?.color1,
      color: cssStyle?.buttonLabel?.color1,
    };
  }

  getColumnName(columnIdOrName: string | undefined): string | undefined {
    return columnIdOrName && this.schemaService.getColumnName(columnIdOrName);
  }
}
